'use client'
import { FC } from 'react'
import { testIds } from '@/root/shared-consts'
import { DateTime } from 'luxon'
import cn from 'classnames'
import { DayPickerProps, Inventory, VoyageInfo } from '@/root/shared-types'
import { useRouteSection } from '@/root/modules/shared/SelectDateTimePage'
import { useGetTranslations } from '@/root/shared-hooks'
import { LocalPassengerIcon, BidirectionIcon } from '@/root/ui/shared'
import DayPicker from '@/root/components/shared/DayPicker/DayPicker'
import { ActionButton } from '@/root/modules/tuuleliinid/SelectDateTimePage/components/ActionButton'
import { DepartureRow } from '@/root/modules/tuuleliinid/SelectDateTimePage/components/DepartureRow'
import { DangerGoodsIcon } from '@/icons/DangerGoodsIcon'
import { AnchorIcon } from '@/icons/AnchorIcon'
import { ClockIcon } from '@/icons/ClockIcon'
import { BicycleIcon } from '@/icons/BicycleIcon'
import { CarIcon } from '@/icons/CarIcon'
import { InfoIcon } from '@/icons/InfoIcon'
import { PassengerIcon } from '@/icons/PassengerIcon'
import { CalendarIcon } from '@/root/ui/tuuleliinid/icons/CalendarIcon'
import { SoldOutIcon } from '@/root/ui/tuuleliinid/icons/SoldOutIcon'
import { Loader } from '@/root/ui/tuuleliinid'

interface RouteSectionProps extends DayPickerProps {
  inventory: Inventory
  isLoading: boolean
  routeInfo: VoyageInfo
  sailRefId: number
  setRefIdAndTime: (sailRefId: number, time: string, isNextDay: boolean) => void
  swapPorts?: () => void
  time: string
  hideHeader?: boolean
  initialTimestamp?: DateTime
  routeAttributes?: Record<string, boolean | string>
  showSwapPorts?: boolean
  // for edit section
  requiredPassengers?: number
  requiredResidentPassengers?: number
  requiredVehicles?: number
  requiredResidentVehicles?: number
  requiredBicycles?: number
  onInsufficientCapacityClick?: (reasons: string[]) => void
  disabled?: boolean
}

const ROW_HEIGHT = 56
const GRID_TEMPLATE = 'grid grid-cols-[1fr_90px_1fr] md:grid-cols-[200px_1fr_1fr] pe-5 md:pe-12 w-full'

export const RouteSection: FC<RouteSectionProps> = ({
  routeInfo,
  inventory,
  isLoading,
  sailRefId,
  setRefIdAndTime,
  selectedDate,
  onDayClick,
  isDayDisabled,
  time,
  initialTimestamp,
  routeAttributes,
  swapPorts,
  showSwapPorts = true,
  hideHeader = false,
  requiredPassengers,
  requiredResidentPassengers,
  requiredVehicles,
  requiredResidentVehicles,
  requiredBicycles,
  onInsufficientCapacityClick,
  disabled,
}) => {
  const { getTranslation } = useGetTranslations()
  const {
    durationMessage,
    isCalendarOpen,
    toggleCalendar,
    calendarRef,
    handleDayClick,
    handleSwapPorts,
    hasUpcomingSail,
    mergedData,
    showColumns,
    showDangerGoods,
    showAnchor,
    showSoldOut,
    soldOutWarning,
    selectedDateLabel,
    selectedIndex,
    routeName,
    getAvailability,
    isSailUnavailable,
  } = useRouteSection({
    inventory,
    routeInfo,
    sailRefId,
    selectedDate,
    onDayClick,
    initialTimestamp,
    routeAttributes,
    swapPorts,
    requiredPassengers,
    requiredResidentPassengers,
    requiredVehicles,
    requiredResidentVehicles,
    requiredBicycles,
  })

  return (
    <div
      className={
        hideHeader
          ? 'flex w-full flex-col'
          : 'flex w-full flex-col gap-8 lg:flex-row lg:items-start lg:justify-between lg:gap-6'
      }
    >
      {/* Top/Left Side - Route Info */}
      {!hideHeader && (
        <div className="flex w-full flex-col gap-y-5 md:flex-row md:justify-between md:gap-y-0 lg:w-auto lg:flex-col lg:gap-y-10">
          {/* Route Name */}
          <div className="flex flex-col">
            <div className="flex items-center gap-5">
              <div
                data-testid={testIds.sailSelect.routeName}
                className="text-neutral-darkest text-2xl leading-none font-bold uppercase"
              >
                {routeName}
              </div>
              {showSwapPorts && (
                <ActionButton onClick={handleSwapPorts} data-testid={testIds.sailSelect.changeDirectionButton}>
                  <BidirectionIcon width={24} height={24} className="rotate-90" color="white" />
                </ActionButton>
              )}
            </div>
            {hasUpcomingSail && (
              <div data-testid={testIds.sailSelect.travelTime} className="text-neutral-darkest text-sm leading-tight">
                {durationMessage}
              </div>
            )}
          </div>

          {/* Date Selector */}
          <div className="relative flex flex-col" ref={calendarRef}>
            <div className="flex items-center gap-5">
              <div
                data-testid={testIds.sailSelect.selectedDate}
                className="text-neutral-darkest text-2xl leading-none font-bold uppercase"
              >
                {selectedDateLabel}
              </div>
              <ActionButton onClick={toggleCalendar} data-testid={testIds.sailSelect.selectDateButton}>
                <CalendarIcon width={24} height={24} color="white" />
              </ActionButton>
            </div>
            {hasUpcomingSail && (
              <div className="text-neutral-darkest text-sm leading-tight">
                {getTranslation('schedulePageTableDepartureTime')}: {time}
              </div>
            )}
            {isCalendarOpen && (
              <div>
                <DayPicker
                  className="absolute top-full left-0 z-20 mt-2 rounded-lg bg-white shadow-lg md:right-0 md:left-auto lg:right-auto lg:left-0"
                  selectedDate={selectedDate}
                  onDayClick={handleDayClick}
                  isDayDisabled={isDayDisabled}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Right Side - Departure List */}
      <div
        className={
          hideHeader
            ? 'relative flex w-full flex-col gap-4'
            : 'relative flex w-full flex-1 flex-col items-end gap-4 lg:ps-5'
        }
      >
        {/* Legend Header - hidden on mobile */}
        {hasUpcomingSail && (
          <div className={cn(GRID_TEMPLATE, 'hidden h-10 items-end md:grid')}>
            <div data-testid={testIds.sailSelect.clockIcon} className="flex items-end justify-center">
              <ClockIcon width={24} height={24} color="black" />
            </div>
            <div data-testid={testIds.sailSelect.infoIcon} className="flex w-24 items-end justify-center">
              <InfoIcon width={24} height={24} />
            </div>
            <div className="flex justify-between">
              {showColumns.showPassenger && (
                <div
                  data-testid={testIds.sailSelect.passengerIconHeader}
                  className="flex h-10 w-11 items-end justify-center"
                >
                  <PassengerIcon height={30} />
                </div>
              )}
              {showColumns.showResident && (
                <div
                  data-testid={testIds.sailSelect.residentIconHeader}
                  className="flex h-10 w-11 items-end justify-center"
                >
                  <LocalPassengerIcon width={30} height={30} />
                </div>
              )}
              {showColumns.showBicycle && (
                <div
                  data-testid={testIds.sailSelect.bicycleIconHeader}
                  className="flex h-10 w-11 items-end justify-center"
                >
                  <BicycleIcon width={35} height={22} color="black" />
                </div>
              )}
              {showColumns.showCar && (
                <div
                  data-testid={testIds.sailSelect.vehicleIconHeader}
                  className="flex h-10 w-11 items-end justify-center"
                >
                  <CarIcon width={35} height={17} color="black" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Departure List */}
        {isLoading && !hasUpcomingSail ? (
          <div className="flex w-full justify-center py-10">
            <Loader />
          </div>
        ) : hasUpcomingSail ? (
          <>
            {isLoading && (
              <div className="bg-base-lightest/60 absolute z-20 flex h-full w-full items-center justify-center py-10">
                <Loader />
              </div>
            )}
            <div className="relative flex w-full flex-col gap-px overflow-hidden rounded-2xl bg-white md:gap-0">
              {/* Sliding indicator - desktop only */}
              {selectedIndex !== -1 && (
                <div
                  className="bg-base-medium-light absolute right-0 left-0 z-0 hidden h-14 rounded-lg transition-all duration-300 ease-in-out md:block"
                  style={{ top: `${selectedIndex * ROW_HEIGHT}px` }}
                />
              )}

              {mergedData.map((item, index) => {
                const availability = getAvailability(item)

                return (
                  <DepartureRow
                    key={item.sailRefId}
                    item={item}
                    isSelected={sailRefId === Number(item.sailRefId)}
                    showColumns={showColumns}
                    onSelect={() => setRefIdAndTime(Number(item.sailRefId), item.departureTime, item.isNextDay)}
                    showDivider={index < mergedData.length - 1 && sailRefId !== Number(item.sailRefId)}
                    gridTemplate={GRID_TEMPLATE}
                    insufficientCapacity={isSailUnavailable(item)}
                    onInsufficientCapacityClick={
                      availability && !availability.isAvailable
                        ? () => onInsufficientCapacityClick?.(availability.unavailableReasons)
                        : undefined
                    }
                    disabled={disabled}
                    soldOutText={soldOutWarning}
                  />
                )
              })}
            </div>
          </>
        ) : !isLoading && !hasUpcomingSail ? (
          <div className="flex w-full flex-col rounded-2xl bg-white px-4 py-6">
            <div className="text-neutral-darkest text-center text-sm leading-tight">
              {getTranslation('noMoreSailsTitle')}
            </div>
            <div className="text-neutral-darkest mt-2 text-center text-sm leading-tight">
              {getTranslation('noMoreSailsSubtitle')}
            </div>
          </div>
        ) : null}

        {/* Legend Footer - hidden on mobile */}
        {hasUpcomingSail && (
          <div className="hidden w-full flex-col gap-5 md:flex">
            {showDangerGoods && (
              <div data-testid={testIds.sailSelect.noteAboutDangerous} className="flex items-center gap-4 px-5">
                <DangerGoodsIcon width={24} />
                <div className="text-neutral-darkest text-sm leading-tight">{getTranslation('dangerousGoods')}</div>
              </div>
            )}
            {showAnchor && (
              <div data-testid={testIds.sailSelect.noteAboutCancelled} className="flex items-center gap-4 px-5">
                <AnchorIcon width={24} />
                <div className="text-neutral-darkest text-sm leading-tight">{getTranslation('noteAboutCancelled')}</div>
              </div>
            )}
            {showSoldOut && (
              <div data-testid={testIds.sailSelect.noteAboutSoldOut} className="flex items-center gap-4 px-5">
                <SoldOutIcon width={24} />
                <div className="text-neutral-darkest text-sm leading-tight">{soldOutWarning}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
